import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Barcode, CheckCircle, Lightning, CreditCard, PaperPlaneTilt } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SocialProofStrip from '../components/SocialProofStrip';

const API = process.env.REACT_APP_BACKEND_URL;

const ORDER = ['pallets', 'starter', 'grow', 'core', 'scale', 'business'];
const TIER_BLURB = {
  starter: 'Run your shop without spreadsheets · 2 users · 500 SKUs · 500 orders/mo',
  grow: 'Single-channel SMB ready to scale · 5 users · 2k SKUs · 2.5k orders/mo',
  core: 'Multi-channel ops sweet spot · 15 users · 10k SKUs · 25k orders/mo',
  scale: '3PL & multi-warehouse growth · 50 users · 50k SKUs · 100k orders/mo',
  business: 'Mid-market with audit & compliance · 200 users · 200k SKUs · 500k orders/mo',
};

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialPlan = (searchParams.get('plan') && ORDER.includes(searchParams.get('plan')))
    ? searchParams.get('plan') : 'core';

  const [step, setStep] = useState(1); // 1 company · 2 owner · 3 plan · 4 review
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    company_name: '',
    country: 'AU',
    owner_email: '',
    owner_password: '',
    owner_name: '',
    trial_tier: initialPlan, // backend still reads this field; we now treat it as selected_plan
    accepted_terms: false,
    promo_code: '',
  });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [promoState, setPromoState] = useState({ status: 'idle', payload: null, error: null });

  useEffect(() => { setErr(''); }, [step]);

  const [plansError, setPlansError] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/plans`).then((r) => {
      const filtered = (r.data || []).filter((p) => p.key !== 'enterprise');
      filtered.sort((a, b) => ORDER.indexOf(a.key) - ORDER.indexOf(b.key));
      setPlans(filtered);
      setPlansError(null);
    }).catch((e) => {
      setPlans([]);
      // Most common cause in production is a misconfigured CORS_ORIGINS env
      // var on the backend (the backend doesn't allow this exact origin).
      // Show a clear message so the user doesn't see an empty step.
      setPlansError(
        e?.response?.status
          ? `Couldn't load plans (server responded ${e.response.status}). Please refresh, or contact support@stockaris.com.`
          : `Couldn't load plans — the API isn't reachable from this domain. Please refresh, or contact support@stockaris.com.`
      );
    });
  }, []);

  // Debounced promo-code validation as the user types.
  useEffect(() => {
    const code = (form.promo_code || '').trim().toUpperCase();
    if (!code) { setPromoState({ status: 'idle', payload: null, error: null }); return; }
    setPromoState((s) => ({ ...s, status: 'checking' }));
    const t = setTimeout(async () => {
      try {
        const r = await axios.post(`${API}/api/promo-codes/validate`, { code });
        if (r.data?.valid) setPromoState({ status: 'valid', payload: r.data, error: null });
        else setPromoState({ status: 'invalid', payload: null, error: r.data?.reason || 'invalid' });
      } catch (e) {
        setPromoState({ status: 'invalid', payload: null, error: 'check_failed' });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.promo_code]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const next = () => {
    if (step === 1 && !form.company_name.trim()) return setErr('Company name is required');
    if (step === 2) {
      if (!form.owner_name.trim()) return setErr('Your name is required');
      if (!form.owner_email.includes('@')) return setErr('Valid email required');
      if ((form.owner_password || '').length < 8) return setErr('Password must be 8+ characters');
    }
    if (step === 3 && !ORDER.includes(form.trial_tier)) return setErr('Pick a plan to continue');
    setStep(step + 1);
  };

  const submit = async () => {
    if (!form.accepted_terms) return setErr('You must accept the Terms to continue');
    setBusy(true);
    setErr('');
    try {
      const { data } = await axios.post(`${API}/api/companies/signup`, form, { withCredentials: true });
      // If the user redeemed a free_forever promo, no Stripe needed — straight in.
      if (!data.checkout_url) {
        window.location.href = '/onboarding';
        return;
      }
      // Otherwise redirect to Stripe Checkout. The Stripe success URL
      // will land the user on /billing where the polling endpoint flips
      // the company to active.
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Signup failed');
      setBusy(false);
    }
  };

  const selectedPlan = plans.find((p) => p.key === form.trial_tier);

  return (
    <div className="min-h-screen bg-background flex" data-testid="signup-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-brand p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-card flex items-center justify-center">
              <Barcode weight="bold" className="text-brand" size={28} />
            </div>
            <span className="text-white font-bold text-3xl tracking-tight">STOCKARIS</span>
          </div>
          <p className="mt-4 text-white/80 text-lg">Warehouse Management System</p>
          <div className="mt-10 space-y-3 text-white/90 text-sm">
            {[
              'Pick a plan that fits — change any time',
              'Cancel anytime · no lock-in contracts',
              'Add modules à la carte from the marketplace',
              'Your data stays yours — GDPR / APP 12 export on demand',
            ].map((line) => (
              <div key={line} className="flex items-start gap-2">
                <CheckCircle weight="fill" className="text-white/80 mt-0.5 shrink-0" size={18} />
                <span>{line}</span>
              </div>
            ))}
          </div>
          <div className="mt-10 border border-white/20 bg-white/5 p-4">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/70 mb-1">Not ready to commit?</div>
            <p className="text-white/80 text-xs mb-3">Get a 1:1 walkthrough first. We'll show how Stockaris fits your workflow before you pay.</p>
            <Link
              to="/request-demo"
              data-testid="signup-request-demo-link"
              className="inline-flex items-center gap-2 bg-white text-brand text-xs font-bold uppercase tracking-widest px-4 py-2 hover:bg-white/90"
            >
              <PaperPlaneTilt size={14} weight="bold" /> Request a demo
            </Link>
          </div>
        </div>
        <div className="space-y-4">
          <SocialProofStrip variant="dark" />
          <div className="text-white/60 text-sm">Already have an account? <Link to="/login" className="underline text-white">Sign in →</Link></div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 bg-brand flex items-center justify-center">
              <Barcode weight="bold" className="text-white" size={24} />
            </div>
            <span className="font-bold text-2xl tracking-tight">STOCKARIS</span>
          </div>

          <div className="bg-card border border-border rounded-lg p-8">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-bold">Create your account</h1>
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{step}/4</span>
            </div>
            <p className="text-muted-foreground mb-6 text-sm">Sign up and start shipping smarter today.</p>

            {err && (
              <div data-testid="signup-error" className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm">
                {err}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4" data-testid="signup-step-company">
                <div>
                  <Label htmlFor="cname" className="overline">Company name</Label>
                  <Input id="cname" data-testid="signup-company-name" value={form.company_name} onChange={set('company_name')} placeholder="Acme Logistics" autoFocus />
                </div>
                <div>
                  <Label htmlFor="country" className="overline">Country</Label>
                  <Input id="country" data-testid="signup-country" value={form.country} onChange={set('country')} placeholder="AU" />
                </div>
                <Button data-testid="signup-next-1" onClick={next} className="w-full bg-brand hover:bg-brand/90 text-white py-3 font-bold">Continue</Button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4" data-testid="signup-step-owner">
                <div>
                  <Label className="overline">Your name</Label>
                  <Input data-testid="signup-owner-name" value={form.owner_name} onChange={set('owner_name')} placeholder="Alice Wonderland" autoFocus />
                </div>
                <div>
                  <Label className="overline">Work email</Label>
                  <Input data-testid="signup-owner-email" type="email" value={form.owner_email} onChange={set('owner_email')} placeholder="alice@acme.com" />
                </div>
                <div>
                  <Label className="overline">Password</Label>
                  <Input data-testid="signup-owner-password" type="password" value={form.owner_password} onChange={set('owner_password')} placeholder="At least 8 characters" />
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1 rounded-none">Back</Button>
                  <Button data-testid="signup-next-2" onClick={next} className="flex-1 bg-brand hover:bg-brand/90 text-white font-bold">Continue</Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-3" data-testid="signup-step-tier">
                <p className="text-xs text-muted-foreground uppercase tracking-widest font-bold">Pick a plan. You'll be redirected to Stripe to enter payment.</p>
                {plansError && (
                  <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2" data-testid="signup-plans-error">
                    {plansError}
                  </div>
                )}
                {!plansError && plans.length === 0 && (
                  <div className="text-xs text-muted-foreground bg-muted/40 border border-border px-3 py-2" data-testid="signup-plans-loading">
                    Loading plans…
                  </div>
                )}
                <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                  {plans.map((t) => {
                    const sel = form.trial_tier === t.key;
                    const price = t.price_monthly_usd;
                    return (
                      <button
                        type="button"
                        key={t.key}
                        data-testid={`signup-tier-${t.key}`}
                        onClick={() => set('trial_tier')(t.key)}
                        className={`w-full text-left p-3 border transition ${sel ? 'border-brand bg-brand/5' : 'border-border hover:border-slate-400'}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                              <div className="font-bold text-sm uppercase tracking-wide">{t.name}</div>
                              <div className="text-base font-bold text-brand tabular-nums">A${price}<span className="text-[10px] text-muted-foreground font-mono ml-1">/mo</span><span className="text-[9px] text-muted-foreground font-mono ml-2">GST inc.</span></div>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{TIER_BLURB[t.key]}</p>
                          </div>
                          {sel && <Lightning weight="fill" className="text-brand shrink-0" size={18} />}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="text-[11px] text-muted-foreground border-t border-border pt-2">
                  Need custom limits or white-label? <Link to="/pricing" className="text-brand underline">Compare all 6 tiers →</Link>
                </div>
                <div className="text-[11px] text-muted-foreground">
                  Not ready to pay? <Link to="/request-demo" data-testid="signup-step3-demo-link" className="text-brand underline">Request a demo instead →</Link>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setStep(2)} className="flex-1 rounded-none">Back</Button>
                  <Button data-testid="signup-next-3" onClick={next} className="flex-1 bg-brand hover:bg-brand/90 text-white font-bold">Continue</Button>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-4" data-testid="signup-step-review">
                <p className="text-xs text-muted-foreground uppercase tracking-widest font-bold">Review and pay. You'll be redirected to Stripe.</p>

                <div className="border border-border bg-muted p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Company</span><span className="font-bold">{form.company_name}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Owner</span><span className="font-mono text-xs">{form.owner_email}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Plan</span><span className="font-bold uppercase">{selectedPlan?.name || form.trial_tier}</span></div>
                  <div className="flex justify-between text-base border-t border-border pt-2 mt-2">
                    <span className="font-bold">Total today</span>
                    <span className="font-bold text-brand tabular-nums">A${selectedPlan?.price_monthly_usd ?? '—'}<span className="text-xs text-muted-foreground font-mono ml-1">/mo</span><span className="text-[9px] text-muted-foreground font-mono ml-2">GST inc.</span></span>
                  </div>
                </div>

                <div data-testid="signup-promo-wrap">
                  <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Discount code <span className="font-normal lowercase tracking-normal">(optional)</span></label>
                  <input
                    type="text"
                    data-testid="signup-promo"
                    value={form.promo_code}
                    onChange={(e) => set('promo_code')(e.target.value.toUpperCase())}
                    placeholder="e.g. LAUNCH20"
                    className="w-full mt-1 px-3 py-2 border border-border bg-background font-mono uppercase placeholder:normal-case placeholder:lowercase placeholder:font-sans placeholder:text-muted-foreground focus:outline-none focus:border-brand"
                    autoComplete="off"
                  />
                  {promoState.status === 'checking' && (
                    <div className="text-xs text-muted-foreground mt-1" data-testid="signup-promo-checking">Checking…</div>
                  )}
                  {promoState.status === 'valid' && (
                    <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 mt-1 font-medium" data-testid="signup-promo-valid">
                      ✓ {promoState.payload?.label} — applied
                    </div>
                  )}
                  {promoState.status === 'invalid' && (
                    <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1 mt-1" data-testid="signup-promo-invalid">
                      {(() => {
                        const r = promoState.error;
                        if (r === 'expired')   return 'This code has expired — please ask for a new one.';
                        if (r === 'exhausted') return 'This code has reached its redemption limit.';
                        if (r === 'disabled')  return 'This code has been disabled.';
                        if (r === 'not_found') return 'Code not found — double-check the spelling.';
                        return 'Code not valid — leave blank to continue.';
                      })()}
                    </div>
                  )}
                </div>

                <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="signup-accept-terms">
                  <input
                    type="checkbox"
                    checked={form.accepted_terms}
                    onChange={(e) => set('accepted_terms')(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>I agree to the <Link to="/legal/terms" className="text-brand underline">Terms of Service</Link> and <Link to="/legal/privacy" className="text-brand underline">Privacy Policy</Link>.</span>
                </label>

                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setStep(3)} className="flex-1 rounded-none">Back</Button>
                  <Button
                    data-testid="signup-submit"
                    onClick={submit}
                    disabled={busy || !form.accepted_terms}
                    className="flex-[2] bg-brand hover:bg-brand/90 text-white font-bold"
                  >
                    {busy ? 'Redirecting…' : (
                      <><CreditCard size={14} weight="bold" className="mr-2" /> Continue to payment</>
                    )}
                  </Button>
                </div>
              </div>
            )}

            <p className="mt-6 text-center text-sm text-muted-foreground">
              Already have an account? <Link to="/login" className="text-brand font-semibold hover:underline">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
